import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowClockwise,
  CaretRight,
  ClockCounterClockwise,
  Info,
  ListPlus,
  PencilSimple,
  Plus,
  Users,
} from 'phosphor-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { centralApi, IGrupoResumo, IUsuarioLista } from '../../hooks/useCentralPermissoes';
import { useAuth } from '../../providers/auth';
import { compararComOSistema, useSincronizarTelas, CatalogoConteudo } from './catalogo';
import { CabecalhoCentral } from './CabecalhoCentral';
import { FundoCentral } from './FundoCentral';
import { GrupoCarteira, GrupoGerais } from './GrupoCarteiraGerais';
import { GrupoTelas } from './GrupoTelas';
import { GrupoUsuarios } from './GrupoUsuarios';
import { NovoGrupo } from './NovoGrupo';
import { GrupoDeEmpresasPainel, ListaGruposDeEmpresas } from './GruposDeEmpresas';
import { AlocacaoRapida } from './AlocacaoRapida';
import { GrupoKanbanEventos, GrupoWhatsapp } from './GrupoWhatsappKanban';
import { UsuarioPainel } from './UsuarioPainel';
import { EmpresasInativasPainel } from './EmpresasSituacao';
import { CamposDoCadastroModal } from './CamposExtras';
import { AZUL, Busca, Cartao, Contador, Etiqueta, Eyebrow, Pilula, TEXTO, TEXTO_SUAVE } from './ui';

const GRUPO_ADMIN = 1;
const GRUPO_CLIENTE = 7;

type Aba = 'telas' | 'usuarios' | 'carteira' | 'gerais' | 'whatsapp' | 'kanban';
const ABAS: { aba: Aba; rotulo: string }[] = [
  { aba: 'telas', rotulo: 'Telas e ações' },
  { aba: 'usuarios', rotulo: 'Usuários do grupo' },
  { aba: 'carteira', rotulo: 'Carteira de empresas' },
  { aba: 'gerais', rotulo: 'Permissões gerais' },
  { aba: 'whatsapp', rotulo: 'WhatsApp' },
  { aba: 'kanban', rotulo: 'Kanban de Eventos' },
];

function metaDoGrupo(g: IGrupoResumo) {
  const extra = g.usuarios_adicionais ? ` · +${g.usuarios_adicionais} adicional` : '';
  if (g.id === GRUPO_ADMIN) return `${g.usuarios_ativos} usuários · acesso total`;
  if (g.usuarios_ativos === 0) return `nenhum usuário ativo${extra}`;
  return `${g.usuarios_ativos} ${g.usuarios_ativos === 1 ? 'usuário' : 'usuários'}${extra}`;
}

export function CentralPermissoesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  let modo: 'grupos' | 'usuarios' | 'empresas' | 'catalogo' = 'grupos';
  if (params.get('usuario') || params.get('modo') === 'usuarios') modo = 'usuarios';
  else if (params.get('modo') === 'empresas') modo = 'empresas';
  else if (params.get('modo') === 'catalogo') modo = 'catalogo';
  const grupoEmpresa = params.get('gempresa');
  const vendoInativas = params.get('situacao') === 'inativas';
  const [rascunhos, setRascunhos] = useState<string[]>([]);
  const grupoId = Number(params.get('grupo')) || null;
  const usuarioId = Number(params.get('usuario')) || null;
  const aba = (params.get('aba') as Aba) || 'telas';

  const [buscaGrupo, setBuscaGrupo] = useState('');
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const [buscaTelas, setBuscaTelas] = useState<{ texto: string; n: number }>({ texto: '', n: 0 });
  const [usuarioEscolhido, setUsuarioEscolhido] = useState<IUsuarioLista | null>(null);
  const [situacao, setSituacao] = useState<'ativos' | 'inativos' | 'todos'>('ativos');
  const [camposAbertos, setCamposAbertos] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    const t = setTimeout(() => setBuscaUsuario(buscaDigitada), 300);
    return () => clearTimeout(t);
  }, [buscaDigitada]);

  const { data: catalogo = [], isLoading: lCat } = useQuery({
    queryKey: ['central', 'catalogo'],
    queryFn: centralApi.catalogo,
  });
  const { data: grupos = [], isLoading: lGr } = useQuery({
    queryKey: ['central', 'grupos-resumo'],
    queryFn: centralApi.gruposResumo,
  });
  const { data: usuarios = [], isFetching: lUs } = useQuery({
    queryKey: ['central', 'usuarios', buscaUsuario, situacao],
    queryFn: () =>
      centralApi.usuarios(
        buscaUsuario,
        { ativos: true, inativos: false, todos: undefined }[situacao],
      ),
    enabled: modo === 'usuarios',
  });
  const { data: totaisUsuarios } = useQuery({
    queryKey: ['central', 'usuarios-totais'],
    queryFn: async () => {
      const [ativos, inativos] = await Promise.all([
        centralApi.totalDeUsuarios(true),
        centralApi.totalDeUsuarios(false),
      ]);
      return { ativos, inativos, todos: ativos + inativos };
    },
    enabled: modo === 'usuarios',
  });
  const sincronizar = useSincronizarTelas();
  const dif = useMemo(() => compararComOSistema(catalogo), [catalogo]);

  const colaboradores = grupos.filter(g => g.id !== GRUPO_CLIENTE);
  const grupoAtual =
    colaboradores.find(g => g.id === grupoId) ?? colaboradores.find(g => g.id !== GRUPO_ADMIN);
  const naLista = usuarios.find(u => u.id === usuarioId) ?? null;
  // aberto por link (ex.: "Abrir usuário" no grupo) e fora da lista carregada
  const { data: usuarioPorId } = useQuery({
    queryKey: ['central', 'usuario', usuarioId],
    queryFn: () => centralApi.usuario(usuarioId as number),
    enabled: !!usuarioId && !naLista && usuarioEscolhido?.id !== usuarioId,
  });
  const usuarioAtual =
    naLista ?? (usuarioEscolhido?.id === usuarioId ? usuarioEscolhido : (usuarioPorId ?? null));

  const irPara = (novo: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    Object.entries(novo).forEach(([k, v]) => (v === null ? p.delete(k) : p.set(k, v)));
    setParams(p, { replace: true });
  };

  if (lCat || lGr) {
    return (
      <FundoCentral>
        <Flex justify="center" py={24}>
          <Spinner color={AZUL} size="lg" />
        </Flex>
      </FundoCentral>
    );
  }

  return (
    <FundoCentral>
      <CabecalhoCentral atual={modo} />

      {dif.total > 0 && (
        <Flex
          align="center"
          gap="12px"
          wrap="wrap"
          p="12px 16px"
          borderRadius="18px"
          bg="rgba(223,243,255,.8)"
          color="#075985"
        >
          <Info size={18} />
          <Text flex="1 1 320px" fontSize="13px" lineHeight="18px">
            <strong>
              {dif.entram.length > 0 &&
                `${dif.entram.length} ${dif.entram.length === 1 ? 'tela nova' : 'telas novas'}`}
              {dif.entram.length > 0 && dif.acoesNovas.length > 0 && ' e '}
              {dif.acoesNovas.length > 0 &&
                `ações novas em ${dif.acoesNovas.length} ${dif.acoesNovas.length === 1 ? 'tela' : 'telas'}`}
              {dif.entram.length === 0 &&
                dif.acoesNovas.length === 0 &&
                `${dif.saem.length} ${dif.saem.length === 1 ? 'tela saiu' : 'telas saíram'} do sistema`}
            </strong>{' '}
            ainda não {dif.entram.length + dif.acoesNovas.length === 1 ? 'está' : 'estão'} no
            catálogo para liberar
            {dif.entram.length > 0 &&
              `: ${dif.entram
                .slice(0, 3)
                .map(t => t.nome)
                .join(', ')}${dif.entram.length > 3 ? '…' : ''}`}
            .
          </Text>
          <Button
            size="sm"
            variant="link"
            color={AZUL}
            onClick={() => irPara({ modo: 'catalogo', usuario: null, grupo: null, gempresa: null })}
          >
            Revisar
          </Button>
          <Button
            size="sm"
            borderRadius="999px"
            leftIcon={<ArrowClockwise size={14} />}
            color="white"
            bg={AZUL}
            _hover={{ bg: '#006D9E' }}
            isLoading={sincronizar.isPending}
            onClick={() => sincronizar.mutate()}
          >
            Sincronizar agora
          </Button>
        </Flex>
      )}

      <Flex gap="20px" align="flex-start" wrap="wrap">
        <Cartao
          as="section"
          aria-label="Quem recebe a permissão"
          flex="1 1 280px"
          maxW={{ base: '100%', lg: '320px' }}
          minW={0}
          p="18px"
          display="flex"
          flexDirection="column"
          gap="14px"
        >
          {modo === 'empresas' && (
            <ListaGruposDeEmpresas
              selecionado={grupoEmpresa}
              rascunhos={rascunhos}
              onSelecionar={nome => irPara({ gempresa: nome, situacao: null })}
              onAlocar={() => irPara({ gempresa: null, situacao: null })}
              onNovo={nome => {
                setRascunhos(r => [...r, nome]);
                irPara({ gempresa: nome, situacao: null });
              }}
              inativasAbertas={vendoInativas}
              onVerInativas={() => irPara({ gempresa: null, situacao: 'inativas' })}
            />
          )}
          {modo === 'grupos' && (
            <>
              <Busca valor={buscaGrupo} onChange={setBuscaGrupo} placeholder="Buscar grupo" />
              <Flex align="center" justify="space-between" gap="8px" wrap="wrap">
                <Eyebrow>GRUPOS DE COLABORADORES</Eyebrow>
                <NovoGrupo
                  nomesExistentes={grupos.map(g => g.nome)}
                  onCriado={id => irPara({ grupo: String(id), aba: 'telas' })}
                />
              </Flex>
              <Flex direction="column" gap="4px">
                {colaboradores
                  .filter(g => g.nome.toLowerCase().includes(buscaGrupo.trim().toLowerCase()))
                  .map(g => {
                    const sel = g.id === grupoAtual?.id;
                    return (
                      <Flex
                        key={g.id}
                        as="button"
                        type="button"
                        aria-pressed={sel}
                        onClick={() => irPara({ grupo: String(g.id) })}
                        align="center"
                        justify="space-between"
                        gap="10px"
                        w="100%"
                        p="10px 12px"
                        borderRadius="14px"
                        textAlign="left"
                        bg={sel ? '#DFF3FF' : 'transparent'}
                        boxShadow={sel ? 'inset 0 0 0 1px rgba(0,119,182,.25)' : undefined}
                        _hover={sel ? undefined : { bg: 'rgba(255,255,255,.7)' }}
                      >
                        <Box minW={0}>
                          <Text fontSize="14px" fontWeight={600} color={TEXTO}>
                            {g.nome}
                          </Text>
                          <Text fontSize="12px" color={TEXTO_SUAVE}>
                            {metaDoGrupo(g)}
                          </Text>
                        </Box>
                        <Contador>{g.id === GRUPO_ADMIN ? 'tudo' : g.telas_liberadas}</Contador>
                      </Flex>
                    );
                  })}
              </Flex>
              <Box borderTop="1px solid rgba(15,23,42,.07)" pt="12px">
                <Eyebrow>ÁREA DO CLIENTE</Eyebrow>
                <Flex
                  as="button"
                  type="button"
                  mt="6px"
                  w="100%"
                  align="center"
                  justify="space-between"
                  gap="8px"
                  p="10px 12px"
                  borderRadius="14px"
                  bg="rgba(254,252,232,.85)"
                  boxShadow="inset 0 0 0 1px #FDE68A"
                  textAlign="left"
                  onClick={() => navigate('/configuracoes/clientes')}
                >
                  <Box>
                    <Text fontSize="14px" fontWeight={600} color="#92400E">
                      Clientes da Área VIP
                    </Text>
                    <Text fontSize="12px" color="#92400E">
                      Portal separado: telas próprias, por cliente
                    </Text>
                  </Box>
                  <CaretRight size={16} color="#92400E" />
                </Flex>
              </Box>
            </>
          )}
          {modo === 'catalogo' && (
            <>
              <Eyebrow>CATÁLOGO DE TELAS</Eyebrow>
              <Text fontSize="13px" color={TEXTO_SUAVE}>
                Todas as telas do sistema e as ações de cada uma. Uma tela nova só aparece para
                liberar nos grupos e usuários depois de entrar no catálogo.
              </Text>
              <Flex wrap="wrap" gap="6px">
                <Contador>{catalogo.filter(t => t.ativo).length} ativas</Contador>
                <Contador>{catalogo.filter(t => !t.ativo).length} inativas</Contador>
                {dif.total > 0 && <Contador tom="ambar">{dif.total} a sincronizar</Contador>}
              </Flex>
            </>
          )}
          {modo === 'usuarios' && (
            <>
              <Flex gap="6px" wrap="wrap">
                <Button
                  size="sm"
                  borderRadius="999px"
                  variant="outline"
                  bg="rgba(255,255,255,.7)"
                  leftIcon={<Plus size={14} />}
                  onClick={() => navigate('/configuracoes/usuarios/criar')}
                >
                  Novo usuário
                </Button>
                {isAdmin() && (
                  <Button
                    size="sm"
                    borderRadius="999px"
                    variant="outline"
                    bg="rgba(255,255,255,.7)"
                    leftIcon={<ListPlus size={14} />}
                    onClick={() => setCamposAbertos(true)}
                  >
                    Campos do cadastro
                  </Button>
                )}
              </Flex>
              <Busca
                valor={buscaDigitada}
                onChange={setBuscaDigitada}
                placeholder="Buscar por nome ou e-mail"
              />
              <Flex gap="6px" wrap="wrap">
                {(
                  [
                    ['ativos', 'Ativos'],
                    ['inativos', 'Inativos'],
                    ['todos', 'Todos'],
                  ] as const
                ).map(([valor, rotulo]) => (
                  <Pilula
                    key={valor}
                    ativa={situacao === valor}
                    onClick={() => setSituacao(valor)}
                    contagem={totaisUsuarios?.[valor]}
                  >
                    {rotulo}
                  </Pilula>
                ))}
              </Flex>
              <Flex direction="column" gap="4px" maxH="62vh" overflowY="auto">
                {lUs && usuarios.length === 0 && <Spinner size="sm" color={AZUL} />}
                {usuarios.map(u => {
                  const sel = u.id === usuarioId;
                  const g = grupos.find(x => x.id === u.grupo_id);
                  return (
                    <Flex
                      key={u.id}
                      as="button"
                      type="button"
                      aria-pressed={sel}
                      onClick={() => {
                        setUsuarioEscolhido(u);
                        irPara({ usuario: String(u.id) });
                      }}
                      align="center"
                      gap="10px"
                      p="9px 10px"
                      borderRadius="14px"
                      textAlign="left"
                      bg={sel ? '#DFF3FF' : 'transparent'}
                      boxShadow={sel ? 'inset 0 0 0 1px rgba(0,119,182,.25)' : undefined}
                      _hover={sel ? undefined : { bg: 'rgba(255,255,255,.7)' }}
                    >
                      <Flex
                        w="32px"
                        h="32px"
                        flex="none"
                        borderRadius="50%"
                        align="center"
                        justify="center"
                        fontSize="12px"
                        fontWeight={700}
                        color="#075985"
                        bg="#DFF3FF"
                      >
                        {(u.nome || u.email).slice(0, 2).toUpperCase()}
                      </Flex>
                      <Box minW={0} flex={1}>
                        <Text fontSize="13.5px" fontWeight={600} noOfLines={1}>
                          {u.nome || u.email}
                        </Text>
                        <Text fontSize="12px" color={TEXTO_SUAVE}>
                          {g?.nome ?? `Grupo ${u.grupo_id}`}
                        </Text>
                      </Box>
                      {!u.ativo && <Etiqueta tom="neutro">inativo</Etiqueta>}
                    </Flex>
                  );
                })}
                {!lUs && usuarios.length === 0 && (
                  <Text fontSize="13px" color={TEXTO_SUAVE}>
                    Ninguém encontrado.
                  </Text>
                )}
              </Flex>
            </>
          )}
        </Cartao>

        <Box
          as="section"
          flex="999 1 640px"
          minW={0}
          display="flex"
          flexDirection="column"
          gap="16px"
        >
          {modo === 'usuarios' && usuarioAtual && (
            <UsuarioPainel
              key={usuarioAtual.id}
              usuario={usuarioAtual}
              grupos={grupos}
              catalogo={catalogo}
              onAbrirGrupo={id =>
                irPara({ modo: null, usuario: null, grupo: String(id), aba: 'telas' })
              }
            />
          )}
          {modo === 'usuarios' && !usuarioAtual && (
            <Cartao p="40px" textAlign="center">
              <Text fontSize="14px" color={TEXTO_SUAVE}>
                Escolha um colaborador à esquerda para ver o que ele herda do grupo e as exceções
                dele.
              </Text>
            </Cartao>
          )}
          {modo === 'catalogo' && <CatalogoConteudo />}
          {modo === 'empresas' && vendoInativas && <EmpresasInativasPainel />}
          {modo === 'empresas' &&
            !vendoInativas &&
            (grupoEmpresa ? (
              <GrupoDeEmpresasPainel
                key={grupoEmpresa}
                nome={grupoEmpresa}
                novo={rascunhos.includes(grupoEmpresa)}
                grupos={grupos}
                onRenomeado={nome => irPara({ gempresa: nome })}
                onSalvo={() => setRascunhos(r => r.filter(x => x !== grupoEmpresa))}
              />
            ) : (
              <AlocacaoRapida />
            ))}
          {modo === 'grupos' && grupoAtual && (
            <>
              <Cartao p="20px 22px" display="flex" flexDirection="column" gap="16px">
                <Flex align="center" gap="14px" wrap="wrap">
                  <Flex
                    w="46px"
                    h="46px"
                    borderRadius="14px"
                    align="center"
                    justify="center"
                    bg="#DFF3FF"
                    color={AZUL}
                  >
                    <Users size={22} />
                  </Flex>
                  <Box flex="1 1 260px" minW={0}>
                    <Eyebrow>GRUPO</Eyebrow>
                    <Text fontSize="22px" lineHeight="28px" fontWeight={700} letterSpacing="-.3px">
                      {grupoAtual.nome}
                    </Text>
                    <Text fontSize="13px" color="#475569">
                      {grupoAtual.usuarios_ativos}{' '}
                      {grupoAtual.usuarios_ativos === 1 ? 'usuário ativo' : 'usuários ativos'} ·{' '}
                      {grupoAtual.id === GRUPO_ADMIN
                        ? 'acesso total'
                        : `${grupoAtual.telas_liberadas} telas liberadas`}
                      {grupoAtual.ve_todas_carteiras ? ' · vê todas as carteiras' : ''}
                    </Text>
                  </Box>
                  <Button
                    size="sm"
                    variant="ghost"
                    borderRadius="999px"
                    leftIcon={<ClockCounterClockwise size={14} />}
                    onClick={() =>
                      navigate(
                        `/configuracoes/logs-de-acesso?${new URLSearchParams({
                          visao: 'permissoes',
                          alvo_tipo: 'grupo',
                          alvo_id: String(grupoAtual.id),
                          alvo_nome: grupoAtual.nome,
                        })}`,
                      )
                    }
                  >
                    Histórico
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    borderRadius="999px"
                    bg="rgba(255,255,255,.7)"
                    leftIcon={<PencilSimple size={14} />}
                    onClick={() => navigate(`/configuracoes/grupos/${grupoAtual.id}/editar`)}
                  >
                    Editar grupo
                  </Button>
                </Flex>
                {grupoAtual.id !== GRUPO_ADMIN && (
                  <Flex as="nav" aria-label="Tipo de permissão" wrap="wrap" gap="6px">
                    {ABAS.map(a => (
                      <Pilula
                        key={a.aba}
                        ativa={aba === a.aba}
                        onClick={() => irPara({ aba: a.aba })}
                      >
                        {a.rotulo}
                      </Pilula>
                    ))}
                  </Flex>
                )}
              </Cartao>

              {grupoAtual.id === GRUPO_ADMIN ? (
                <>
                  <Cartao p="24px" display="flex" gap="12px" alignItems="flex-start">
                    <Info size={20} color={AZUL} />
                    <Text fontSize="14px" color="#334155">
                      O Administrador tem acesso a todas as telas, ações e empresas por regra do
                      sistema; não há telas a configurar. Aqui você vê e muda quem é administrador.
                    </Text>
                  </Cartao>
                  <GrupoUsuarios
                    grupo={grupoAtual}
                    grupos={grupos}
                    onAbrirUsuario={id => irPara({ usuario: String(id), modo: null, grupo: null })}
                  />
                </>
              ) : (
                <Box key={grupoAtual.id}>
                  {aba === 'usuarios' && (
                    <GrupoUsuarios
                      grupo={grupoAtual}
                      grupos={grupos}
                      onAbrirUsuario={id =>
                        irPara({ usuario: String(id), modo: null, grupo: null })
                      }
                    />
                  )}
                  <Box display={aba === 'telas' ? 'block' : 'none'}>
                    <GrupoTelas
                      grupoId={grupoAtual.id}
                      grupoNome={grupoAtual.nome}
                      catalogo={catalogo}
                      buscaPedida={buscaTelas}
                    />
                  </Box>
                  {aba === 'carteira' && (
                    <GrupoCarteira grupoId={grupoAtual.id} grupoNome={grupoAtual.nome} />
                  )}
                  {aba === 'gerais' && (
                    <GrupoGerais grupoId={grupoAtual.id} grupoNome={grupoAtual.nome} />
                  )}
                  {aba === 'whatsapp' && (
                    <GrupoWhatsapp
                      grupoId={grupoAtual.id}
                      grupoNome={grupoAtual.nome}
                      onIrParaTelas={texto => {
                        setBuscaTelas(b => ({ texto, n: b.n + 1 }));
                        irPara({ aba: 'telas' });
                      }}
                    />
                  )}
                  {aba === 'kanban' && (
                    <GrupoKanbanEventos
                      grupoId={grupoAtual.id}
                      grupoNome={grupoAtual.nome}
                      onIrParaTelas={texto => {
                        setBuscaTelas(b => ({ texto, n: b.n + 1 }));
                        irPara({ aba: 'telas' });
                      }}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Flex>
      <CamposDoCadastroModal isOpen={camposAbertos} onClose={() => setCamposAbertos(false)} />
    </FundoCentral>
  );
}
